import { ReactNode } from 'react';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SummaryChipProps {
  icon: ReactNode;
  label: string;
  value: string;
  tooltip: ReactNode;
}

/**
 * One card's collapsed rendition: dark round icon, label, value, and the card's own tooltip.
 * Mirrors the overheads collapsed summary.
 */
export const SummaryChip = ({ icon, label, value, tooltip }: SummaryChipProps) => (
  <SimpleTooltip content={tooltip}>
    <button
      type="button"
      className={cn(
        'flex shrink-0 items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5',
        'text-xs font-medium text-foreground transition-colors hover:bg-muted/70',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
      aria-label={`${label}: ${value}`}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
        {icon}
      </span>
      <span className="max-w-[140px] truncate text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </button>
  </SimpleTooltip>
);

/** Loading placeholder for one chip — same pill footprint as {@link SummaryChip}. */
export const SummaryChipSkeleton = () => <Skeleton className="h-8 w-36 shrink-0 rounded-full" />;
